import { AccommodationModalDetailed } from "@/components/AccommodationModalDetailed/AccommodationModalDetailed.tsx";
import { AccommodationSummaryCardProps } from "@/components/AccommodationSummaryCard/AccommodationSummaryCardProps.ts";
import { Close } from "@/components/Icons/utility/Close.tsx";
import { AdultAgeSelect } from "@/components/Inputs/AdultAgeSelect/AdultAgeSelect.tsx";
import { ChildrenAgeSelect } from "@/components/Inputs/ChildrenAgeSelect/ChildrenAgeSelect.tsx";
import { setChildChangeSource } from "@/components/Inputs/ChildrenAgeSelect/childChangeSource.ts";
import NumInput from "@/components/Inputs/NumInput/NumInput.tsx";

import Modal from "@/components/Modal/Modal.tsx";
import { handleGTMRemoveAccommodationFromCart } from "@/helpers/gtm/handleGTMRemoveAccommodationFromCart.ts";
import { useMaxAdults } from "@/helpers/hooks/useMaxAdults.ts";
import { RatePlanMerged } from "@/helpers/interfaces/RatePlanMerged.ts";
import { locales } from "@/helpers/locales.ts";
import { useGetRGTranslated } from "@/helpers/translation/useGetRGTranslated.ts";
import { RateGroup } from "@/helpers/types/types.ts";
import { useModalState } from "@/helpers/useModalState.ts";
import { useEngineStore } from "@/stores/EngineStore/EngineStore.ts";
import format from "date-fns/format";
import i18n from "i18next";
import { ImageOff } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useShallow } from "zustand/react/shallow";

export const AccommodationSummaryCard = ({ item }: AccommodationSummaryCardProps) => {
  const { t } = useTranslation();
  const { name: RGName } = useGetRGTranslated(item.ratePlan.rateGroup);
  const { modalState, handleOpenModal, handleCloseModal } = useModalState<RateGroup>();
  const locale = locales[i18n.language];
  const maxAdults = useMaxAdults();

  const dateRangeLabel =
    item.dateRange[0] && item.dateRange[1]
      ? `${format(new Date(item.dateRange[0]), "dd.MM.yyyy", { locale })} - ${format(
          new Date(item.dateRange[1]),
          "dd.MM.yyyy",
          { locale },
        )}`
      : "";

  const { setCartItemAdults, setCartItemChild, removeCartItem } = useEngineStore(
    useShallow((state) => ({
      setCartItemAdults: state.setCartItemAdults,
      setCartItemChild: state.setCartItemChild,
      removeCartItem: state.removeCartItem,
    })),
  );

  const guestTotalCapacity = (RP: RatePlanMerged): number => {
    if (RP.rateGroup?.guestCapacity && RP.rateGroup?.guestAdditionalCapacity) {
      return RP.rateGroup.guestCapacity + RP.rateGroup.guestAdditionalCapacity;
    }
    return RP.rateGroup.guestCapacity;
  };

  const image =
    item.ratePlan.rateGroup.images?.find((img) => img.isPrimary) ?? item.ratePlan.rateGroup.images?.[0];

  return (
    <>
      <div className="c-accommodation-summary">
        <button
          className="c-accommodation-summary__remove c-close-button"
          onClick={() => {
            removeCartItem(item.id);
            handleGTMRemoveAccommodationFromCart(item);
          }}
        >
          <Close />
        </button>
        <div className="c-accommodation-summary__inner">
          <div
            className={`c-accommodation-summary__image-wrap${
              image ? "" : " c-accommodation-summary__image-wrap--placeholder"
            }`}
            onClick={image ? () => handleOpenModal(item.ratePlan.rateGroup) : undefined}
          >
            {image ? (
              <img className="c-accommodation-summary__image" src={image.url} alt={RGName || ""} />
            ) : (
              <div className="c-accommodation-summary__image-placeholder">
                <ImageOff size={20} strokeWidth={1.5} />
              </div>
            )}
          </div>
          <div className="c-accommodation-summary__details">
            <div className="c-accommodation-summary__top">
              <div className="c-accommodation-summary__title-wrap">
                <h2 className="c-accommodation-summary__title u-h5 u-fw-600">{RGName}</h2>
                <p className="c-accommodation-summary__subtitle u-p1">{dateRangeLabel}</p>
              </div>
            </div>
            <div className="c-accommodation-summary__bottom">
              <div className="c-accommodation-summary__pickers">
                <NumInput
                  label={t("adults")}
                  onChange={(num) => setCartItemAdults(num, item.id)}
                  value={item.adults.length}
                  decrementDisabled={item.adults.length <= 1}
                  incrementDisabled={
                    item.adults.length + item.children.length >= guestTotalCapacity(item.ratePlan) ||
                    item.adults.length >= (maxAdults ?? Infinity)
                  }
                  min={1}
                  max={Math.min(
                    guestTotalCapacity(item.ratePlan) - item.children.length,
                    maxAdults ?? Infinity,
                  )}
                  className="c-accommodation-summary__picker"
                />
                <NumInput
                  label={t("children")}
                  onChange={(num) => {
                    setChildChangeSource("card");
                    setCartItemChild(num, item.id);
                  }}
                  value={item.children.length}
                  incrementDisabled={
                    item.adults.length + item.children.length >= guestTotalCapacity(item.ratePlan)
                  }
                  max={guestTotalCapacity(item.ratePlan) - item.adults.length}
                  className="c-accommodation-summary__picker"
                />
              </div>
            </div>
            <div
              className={`c-accommodation-summary__children-ages${
                item.children.length > 0 ? " is-visible" : ""
              }`}
            >
              <div className="c-accommodation-summary__children-ages-inner">
                <ChildrenAgeSelect cartItem={item} compact autoOpen instanceId="card" />
              </div>
            </div>
            {item.ratePlan.rateGroup.isParcel && <AdultAgeSelect cartItem={item} compact />}
          </div>
        </div>
      </div>
      <Modal isOpen={modalState.open} onClose={handleCloseModal}>
        <AccommodationModalDetailed previewRG={modalState.editObject} handleCloseModal={handleCloseModal} />
      </Modal>
    </>
  );
};
